import { PropTypes } from 'prop-types';
import moment from 'moment';
import { get } from 'lodash';
import { AVAILABILITY_FILTERS, getCurrentTime, hasRule, readRuleAsMoment, readRuleAsBoolean } from './permitUtils';
import { AVAILABILITY_KEY_MAP_FORMAT, GROUP_AVAILABILITY_DAILY_KEY_NAME } from '../constants';

export const isDayWithinSeason = (day, props) => {
  const seasonStart = readRuleAsMoment('SeasonStart', props.permit);
  const seasonEnd = readRuleAsMoment('SeasonEnd', props.permit);

  return seasonStart.isValid() && seasonEnd.isValid() && day.isBetween(seasonStart, seasonEnd, 'days', '[]');
};

export const isDayWithinLotterySeason = (day, props) => {
  const lotterySeasonStart = readRuleAsMoment('HighUseSeasonStart', props.permit);
  const lotterySeasonEnd = readRuleAsMoment('HighUseSeasonEnd', props.permit);

  return lotterySeasonStart.isValid() && lotterySeasonEnd.isValid() && day.isBetween(lotterySeasonStart, lotterySeasonEnd, 'days', '[]');
};

export const isDayManagedByLottery = (day, props) => {
  const dayBlockedByLotteryStart = readRuleAsMoment('DisplayDate', props.permit);
  const dayBlockedByLotteryEnd = readRuleAsMoment('LotteryExecutionDate', props.permit);
  const currentDate = getCurrentTime(props);

  return isDayWithinLotterySeason(day, props) &&
    dayBlockedByLotteryStart.isValid() &&
    dayBlockedByLotteryEnd.isValid() &&
    currentDate.isBetween(dayBlockedByLotteryStart, dayBlockedByLotteryEnd, '[]', true);
};


export const isDayBeingBlockedByLottery = (day, props) => {
  return props.blockLotteryDates && isDayManagedByLottery(day, props);
};

export const isDayBlocked = (day, props) => {
  if (day.isBefore(getCurrentTime(props))) {
    return true;
  }
  if (!isDayWithinSeason(day, props)) {
    return true;
  }
  if (props.blockNonLotteryDates && !isDayWithinLotterySeason(day, props)) {
    return true;
  }
  if (isDayBeingBlockedByLottery(day, props)) {
    return true;
  }
  if (!props.dateMap) {
    return true;
  }
  if (!props.dateMap[day.format(AVAILABILITY_KEY_MAP_FORMAT)]) {
    return true;
  }

  return false;
};

export const findNextAvailableDay = (bools, availabilityProps) => {
  let nextDate;

  Object.keys(bools).forEach((date) => {
    if (!nextDate) {
      if (!isDayBlocked(moment(date).utc(), availabilityProps)) {
        nextDate = new Date(date);
      }
    }
  });

  return nextDate;
};

export const isExtendedStayPresent = (permit) => {
  if (!permit || !permit.divisions) {
    return false;
  }

  const result = Object.keys(permit.divisions).filter((divisionId) => {
    return hasRule('StayLimitOverLengthInDays', permit, divisionId);
  });

  return result.length !== 0;
};


// returns all known quota types
export const readQuotaFilters = (availabilities, permit) => {
  if (!availabilities || !permit || !permit.divisions) {
    return [];
  }
  const quotaTypes = new Set();

  Object.keys(availabilities).forEach((availabilityId) => {
    const availability = availabilities[availabilityId].result;
    if (availability) {
      Object.keys(availability.quota_type_maps).forEach((quotaType) => {
        quotaTypes.add(quotaType);
      });
    }
  });

  const matchingQuotaFilters = AVAILABILITY_FILTERS(permit).filter((quotaType) => {
    return quotaTypes.has(quotaType.type);
  });

  return matchingQuotaFilters;
};

export const seedQuotaFilters = (availabilities, permit, activeFilterType, existingFilters) => {
  const quotaFilters = readQuotaFilters(availabilities, permit);
  const resultingFilterValues = {
    mainFilterType: activeFilterType,
    filterValues: existingFilters
  };

  // set initial filter type
  const noFilter = !activeFilterType;
  const activeFilterIsValid = !!quotaFilters.filter((quotaFilter) => quotaFilter.type === activeFilterType)[0];
  if (quotaFilters.length > 0 && (noFilter || !activeFilterIsValid)) {
    resultingFilterValues.mainFilterType = quotaFilters[0].type;
  }

  // set filter values
  const newFilterValues = {};
  quotaFilters
    .forEach((quotaFilter) => {
      if (existingFilters[quotaFilter.type]) {
        newFilterValues[quotaFilter.type] = existingFilters[quotaFilter.type];
      }
      else {
        newFilterValues[quotaFilter.type] = quotaFilter.initialValue;
      }
    });
  resultingFilterValues.filterValues = newFilterValues;
  return resultingFilterValues;
};

export const filterAvailability = (availability, filters) => {
  const resultingAvailability = Object.assign({}, availability.bools);
  Object.keys(filters).forEach((filterType) => {
    const filterValue = filters[filterType];
    const quotaTypeMap = availability.quota_type_maps[filterType];
    if (!quotaTypeMap) {
      return;
    }

    Object.keys(quotaTypeMap).forEach((rawDate) => {
      const availabilityDateKey = moment(rawDate).utc().format(AVAILABILITY_KEY_MAP_FORMAT);

      const isInitiallyAvailable = resultingAvailability[availabilityDateKey];
      const passesQuotaFilter = quotaTypeMap[rawDate].remaining >= filterValue;
      resultingAvailability[availabilityDateKey] = isInitiallyAvailable && passesQuotaFilter;
    });
  });

  return resultingAvailability;
};

export const getFilterErrors = (filterValues, permit) => {
  const errors = Object.keys(filterValues)
    .map((quotaFilterType) => {
      if (!filterValues[quotaFilterType] && filterValues[quotaFilterType] !== 0) {
        const filter = AVAILABILITY_FILTERS(permit).find((availabilityFilter) => quotaFilterType === availabilityFilter.type);
        return get(filter, 'missingInputError');
      }
      return undefined;
    })
    .filter((errorMessage) => !!errorMessage);
  return errors;
};

export const ignoreQuotaAfterFirstDay = (availability) => {
  if (!availability || !availability.quota_type_maps) {
    return true;
  }

  return availability.quota_type_maps[GROUP_AVAILABILITY_DAILY_KEY_NAME] === undefined;
};

export const AVAILABILITY_PROPTYPES = {
  permit: PropTypes.shape({
    indexed_rules: PropTypes.shape({
      SeasonStart: PropTypes.object,
      SeasonEnd: PropTypes.object,
      HighUseSeasonStart: PropTypes.object,
      HighUseSeasonEnd: PropTypes.object,
      DisplayDate: PropTypes.object,
      LotteryExecutionDate: PropTypes.object
    })
  }),
  currentDate: PropTypes.object,
  blockLotteryDates: PropTypes.bool,
  dateMap: PropTypes.object
};

// Checks if account is commercial and has access to the permit
export function hasAccessToPermit(account, permitId) {
  if (account.is_commercial_account) {
    const availablePermit = get(account, ['special_use_permits', permitId]);
    return availablePermit && !availablePermit.is_suspended && moment(availablePermit.expiration).isAfter(moment());
  }
  return true;
}

// Checks if account is commercial and has access to lottery at the permit
export function hasAccessToLottery(account, permit) {
  if (account.is_commercial_account) {
    return readRuleAsBoolean('CommercialLotteryAllowed', permit);
  }
  return true;
}
